import React from 'react';
import { cn } from '../../lib/utils';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Plus, Users, Wallet } from 'lucide-react';
import { useExpenses } from './hooks/useExpenses';
import { ExpensesTable, ExpenseDialog, PayersDialog } from './components';
import { ExpensesPageProps } from './types';

export default function ExpensesPage({ isDark }: ExpensesPageProps) {
  const {
    expenses,
    summary,
    payers,
    isLoading,
    isCreateDialogOpen,
    isEditDialogOpen,
    isPayersDialogOpen,
    formData,
    newPayerName,
    isSubmitting,
    setIsCreateDialogOpen,
    setIsEditDialogOpen,
    setIsPayersDialogOpen,
    setFormData,
    setNewPayerName,
    openCreate,
    handleCreate,
    handleEdit,
    handleUpdate,
    handleDelete,
    handleAddPayer,
    handleDeletePayer,
  } = useExpenses();

  const formatMoney = (amount: number) =>
    new Intl.NumberFormat('uz-UZ', {
      style: 'currency',
      currency: 'UZS',
      minimumFractionDigits: 0,
    }).format(amount);

  const cardCls = cn(
    'rounded-2xl border-0 p-5 transition-all duration-300',
    isDark ? 'bg-[#1A1A22] shadow-lg shadow-black/10' : 'bg-white shadow-lg shadow-slate-200/50',
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={cn('text-2xl lg:text-3xl font-bold tracking-tight', isDark ? 'text-white' : 'text-[#111111]')}>
            Расходы
          </h1>
          <p className={cn('text-sm mt-1', isDark ? 'text-slate-400' : 'text-slate-500')}>
            Учёт расходов и кто сколько заплатил
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setIsPayersDialogOpen(true)}
            className={cn(isDark ? 'border-slate-700 text-slate-300 hover:bg-slate-800' : 'border-slate-200')}
          >
            <Users className="w-4 h-4 mr-2" />
            Плательщики
          </Button>
          <Button onClick={openCreate} className="bg-[#C9A227] hover:bg-[#C9A227]/90">
            <Plus className="w-4 h-4 mr-2" />
            Добавить расход
          </Button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className={cn(cardCls, 'md:col-span-1')}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#C9A227]/10 flex items-center justify-center">
              <Wallet className="w-5 h-5 text-[#C9A227]" />
            </div>
            <div>
              <p className={cn('text-xs', isDark ? 'text-slate-400' : 'text-slate-500')}>Всего потрачено</p>
              <p className={cn('text-xl font-bold', isDark ? 'text-white' : 'text-[#111111]')}>
                {formatMoney(summary.total)}
              </p>
            </div>
          </div>
        </Card>

        <Card className={cn(cardCls, 'md:col-span-2')}>
          <p className={cn('text-xs mb-3', isDark ? 'text-slate-400' : 'text-slate-500')}>По плательщикам</p>
          {summary.byPayer.length === 0 ? (
            <p className="text-sm text-slate-500">Нет данных</p>
          ) : (
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              {summary.byPayer.map((row) => (
                <div key={row.payerId}>
                  <p className={cn('text-sm font-medium', isDark ? 'text-slate-300' : 'text-slate-700')}>
                    {row.payerName}
                  </p>
                  <p className={cn('text-base font-bold', isDark ? 'text-white' : 'text-[#111111]')}>
                    {formatMoney(row.total)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Table */}
      <Card className={cn(
        'rounded-2xl border-0 overflow-hidden transition-all duration-300',
        isDark ? 'bg-[#1A1A22] shadow-lg shadow-black/10' : 'bg-white shadow-lg shadow-slate-200/50',
      )}>
        <ExpensesTable
          isDark={isDark}
          expenses={expenses}
          isLoading={isLoading}
          onEdit={handleEdit}
          onDelete={handleDelete}
          formatMoney={formatMoney}
        />
      </Card>

      {/* Dialogs */}
      <ExpenseDialog
        isDark={isDark}
        isOpen={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        payers={payers}
        formData={formData}
        onFormDataChange={setFormData}
        onSubmit={handleCreate}
        isSubmitting={isSubmitting}
        title="Новый расход"
        description="Добавьте расход и укажите, кто платил"
        submitLabel="Добавить"
      />
      <ExpenseDialog
        isDark={isDark}
        isOpen={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        payers={payers}
        formData={formData}
        onFormDataChange={setFormData}
        onSubmit={handleUpdate}
        isSubmitting={isSubmitting}
        title="Редактировать расход"
        description="Обновите данные расхода"
        submitLabel="Сохранить"
      />
      <PayersDialog
        isDark={isDark}
        isOpen={isPayersDialogOpen}
        onOpenChange={setIsPayersDialogOpen}
        payers={payers}
        newPayerName={newPayerName}
        onNewPayerNameChange={setNewPayerName}
        onAddPayer={handleAddPayer}
        onDeletePayer={handleDeletePayer}
      />
    </div>
  );
}
